module.exports = class ZPusher {

    /**
     * @param {String}  endpoint    - WebSocket endpoint
     * @param {String}  token       - Токен приложения, заранее полученый с сервера
     * @param {Boolean} autoconnect - Флаг, показывающий нужно ли инициализовать соединение автоматически при создании экземпляра класса
     * @param {Boolean} reconnect   - Флаг, показывающий необходимо ли переподключение при разывах соединения
     * @param {Number}  reconnectTimeout - Время в секундах, через которое будет выполненна попытка повторного соединения
     * 
     * @returns Pusher Object
     */
    constructor({endpoint, token, autoconnect, reconnect, reconnectTimeout}) {
        this.endpoint   = endpoint || null;
        this.token      = token    || null;
        this.WSEndpoint = (this.endpoint + '?token=' + this.token);

        this.reconnectTimeout = (reconnectTimeout === undefined || parseInt(reconnectTimeout) <= 10) ? 30000 : (parseInt(reconnectTimeout) * 1000);
        this.reconnect   = (reconnect   === undefined) ? true : !!reconnect;
        this.autoconnect = (autoconnect === undefined) ? true : !!autoconnect;

        this.listeners      = {};
        this.eventListeners = {};

        if (!this.endpoint || !this.token) throw new Error('[Pusher]: endpoint or token is not defined');

        //setTimeout нужен чтобы connect() вызывался на следующем тике JS Event Loop
        //т.к. за это время клиентский код успеет повешать слушатели
        if (this.autoconnect) setTimeout(() => this.connect());
    }

    /**
     * Очищает список слушателей т.к.
     * при реконнекте они вешаются заново на новое соединение
     * 
     * @returns void
     */
    flushEventListeners() {
        this.eventListeners = {};
        delete this.listeners['message'];
    }

    /**
     * Инициализует соединение с WS сервером
     * вешает слушатели для обработки соединения
     * 
     * @returns Promise
     */
    connect() {
        return new Promise((resolve, reject) => {
            this.socket = new WebSocket(this.WSEndpoint);
            this.socket.onopen = (e) => this.waitForConnection(() => {
                this.onOpen(e)
                // Отправляем сообщения на подписку повторно (это нужно, если произошёл реконнект)
                if (this.eventListeners && Object.keys(this.eventListeners)) {
                    let channels = Object.keys(this.eventListeners).map(key => key.split('.').slice(0, -1).join('.'));
                    if (!channels || !channels.length) return
                    channels = new Set(channels);
                    Array.from(channels).forEach(key => this.subscribe(key))
                }
            })
            this.socket.onclose   = (e) => this.onClose(e);
            this.socket.onerror   = (e) => this.onError(e);
            this.socket.onmessage = (e) => this.onMessage(e);

            this.on('open',  e => resolve(e, this));
            this.on('close', e => reject(e, this));
            this.on('error', e => reject(e, this));
        });
    }

    waitForConnection(callback) {
        if (this.socket.readyState === 1) callback();
        else setTimeout(() => this.waitForConnection(callback), 100);
    }

    /**
     * Разрывает соединение с сервером
     * @returns void
     */
    close() {
        if (!this.socket) return false
        this.socket.close()
        return true
    }

    /**
     * Пингует сервер раз в минуту (иначе барузер закрывает соединение)
     * @returns void
     */
    pingLoop() {
        this.clearPingLoop();
        this.pingInterval = setInterval(() => this.send('ping'), 60000);
    }

    /**
     * Очищает цикл пинга
     */
    clearPingLoop() {
        if (this.pingInterval) clearInterval(this.pingInterval);
    }

    /**
     * Callback для события открытия соединения
     * 
     * @param {Event} e - Событие
     * @returns void
     */
    onOpen(e) {
        this.emit('open', e);
        this.pingLoop();
        clearTimeout(this.reconnecting);
    }

    /**
     * Callback для события закрытия соединения
     * 
     * @param {Event} e - Событие
     * @returns void
     */
    onClose(e) {
        this.emit('close', e);
        this.clearPingLoop();
        if (this.reconnect) this.reconnecting = setTimeout(() => {
            this.emit('reconnect');
            this.connect();
        }, this.reconnectTimeout);
    }

    /**
     * Callback для события ошибки во время открытия соединения
     * 
     * @param {Event} e - Событие
     * @returns void
     */
    onError(e) {
        this.emit('error', e);
    }

    /**
     * Callback для события получения сообщения из соединения
     * 
     * @param {Event} e - Событие
     * @returns void
     */
    onMessage(e) {
        this.emit('message', JSON.parse(e.data), e);
    }

    /**
     * Вешает слушатель указанного события
     * (работает только с событиями  на текущем объекте,
     * не используется для обработки сообщений из WebSocket соединения)
     * 
     * @param {String}   event    - Текстовый код события
     * @param {Function} callback - Callback функция
     * @returns void
     */
    on(event, callback) {
        if (!this.listeners[event]) this.listeners[event] = [];
        this.listeners[event].push(callback);
    }

    /**
     * Вызывает callback'и на переданное событие
     * (работает только с событиями  на текущем объекте,
     * не используется для обработки сообщений из WebSocket соединения)
     * 
     * @param {String} event - Текстовый код события
     * @param {Mixed}  data  - Данные, которые будут переданы в callback
     * @returns void
     */
    emit(event, data) {
        if (!this.listeners[event]) return;
        this.listeners[event].map(callback => callback(data));
    }

    /**
     * Вешает слушатель указанного WebSocket события
     * (работает только с событиями WebSocket соединения,
     * не используется для обработки сообщений на текущем объекте)
     * 
     * @param {String} event - Текстовый код события
     * @param {Function} callback - Callback функция
     * @returns void
     */
    onEvent(channel, event, callback) {
        const k = `${channel}.${event}`
        if (!this.eventListeners[k]) this.eventListeners[k] = [];
        this.eventListeners[k].push(callback);
    }

    /**
     * Вызывает callback'и на переданное событие
     * (работает только с событиями WebSocket соединения,
     * не используется для обработки сообщений на текущем объекте)
     * 
     * @param {String} event - Текстовый код события
     * @param {Mixed}  data  - Данные, которые будут переданы в callback
     * @returns void
     */
    emitEvent(channel, event, data) {
        const k = `${channel}.${event}`
        if (!this.eventListeners[k]) return;
        this.eventListeners[k].map(callback => callback(data));
    }

    /**
     * Реализует механизм подписки на каналы WebSocket соединения
     * 
     * @param {String} channel - Название канала для подписки
     * @returns Promise
     */
    subscribe(channel) {
        return new Promise((resolve, reject) => {
            const timeOut = setTimeout(() => reject(), 30000);
            
            this.on('message', e => {
                if (!e || e.channel != channel) return;
                if (e.event == 'subscribed') {
                    clearTimeout(timeOut);
                    resolve((event, callback) => this.onEvent(channel, event, callback));
                } else this.emitEvent(e.channel, e.event, e.data);
            });

            this.send('subscribe', channel, {channel: channel});
        });
    }

    /**
     * Отправляет сообщение через WebSocket соединение на сервер
     * 
     * @param {String} event   - Событие
     * @param {String} channel - Название канала
     * @param {Mixed}  data    - Данные, для отправки
     * @returns void
     */
    send(event, channel, data = {}) {
        return new Promise(resolve => this.waitForConnection(() => {
            this.socket.send(JSON.stringify({ event: event, channel: channel, data: data }));
            resolve();
        }));
    }
};


//////////////////
// WEBPACK FOOTER
// ./~/zpusher-client/index.js
// module id = 1292
// module chunks = 1